import { callSseApi, type SSECallbacks } from '@front/repository/common/sse';
import { AuthDomain } from 'auth-lib-front';
import { useCallback, useRef, useState } from 'react';
import type { CsvAnalysis, CsvAnalyzeStreamModelId } from 'troppus-common/shared';

import Project = AuthDomain.Object.Project;

export type AnalyzeCsvStreamInput = {
  fileId: string;
  /** CSV 列推定ストリーム用の LLM。未指定時はサーバのデフォルト。選択肢は `CSV_ANALYZE_STREAM_MODEL_OPTIONS`。 */
  modelId?: CsvAnalyzeStreamModelId;
};

export type AnalyzeCsvStreamCallbacks = SSECallbacks<CsvAnalysis.StreamEvent>;

export type UseAnalyzeCsvStreamReturn = {
  start: (input: AnalyzeCsvStreamInput, callbacks: AnalyzeCsvStreamCallbacks) => void;
  abort: () => void;
  isPending: boolean;
};

type StartRequest = {
  projectId: string;

  fileId: string;
  modelId?: string;
};

/**
 * /files/analyze-stream を叩く SSE ストリーム hook。
 * `reasoning-delta` を逐次受信しつつ、最終的に `done` で `CsvAnalysis` を受け取る。
 */
export const _useAnalyzeCsvStream = (projectId: Project.Id): UseAnalyzeCsvStreamReturn => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isPending, setIsPending] = useState(false);

  const start = useCallback(
    (input: AnalyzeCsvStreamInput, callbacks: AnalyzeCsvStreamCallbacks) => {
      abortControllerRef.current?.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsPending(true);

      const body: StartRequest = { projectId, fileId: input.fileId, ...(input.modelId !== undefined ? { modelId: input.modelId } : {}) };

      callSseApi<CsvAnalysis.StreamEvent, StartRequest>('/files/analyze-stream', callbacks, body, controller.signal)
        .catch((error) => {
          if (controller.signal.aborted) return;
          callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));
        })
        .finally(() => {
          if (abortControllerRef.current === controller) {
            abortControllerRef.current = null;
            setIsPending(false);
          }
        });
    },
    [projectId],
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsPending(false);
  }, []);

  return { start, abort, isPending };
};
